import { useEffect, useMemo, useState } from 'react';
import { sortHand, type CribbageLegal, type CribbageShow, type CribbageView } from '@cardhub/engine';
import { PlayingCard, shortCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, SeatView, useNarrow, useSpeech } from './common';

const ITEM_ORDER = ['fifteen', 'pair', 'run', 'flush', 'nobs'] as const;

export function CribbageTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as CribbageView;
  const legal = useGame((s) => s.legal) as CribbageLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const [picked, setPicked] = useState<string[]>([]);
  const narrow = useNarrow();
  const me = mySeat ?? 0;
  const opp = 1 - me;
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const discarding = Boolean(myTurn && legal?.kind === 'discard');
  const playable = new Set(myTurn && legal?.kind === 'play' ? legal.cards : []);
  const target = view.config.targetScore;
  const lastShow = view.shown.at(-1);

  useEffect(() => {
    setPicked([]);
  }, [view.round, view.phase, view.turn]);

  const onCard = (c: string) => {
    if (discarding) setPicked((p) => (p.includes(c) ? p.filter((x) => x !== c) : p.length >= 2 ? [p[1]!, c] : [...p, c]));
    else if (playable.has(c) && !acting) void act({ type: 'play', card: c });
  };

  const reasonLabel = (r: string) => {
    if (r.startsWith('run')) return t('cg.r.run', { n: r.slice(3) });
    return t(`cg.r.${r}` as never);
  };

  const track = (seat: number) => {
    const pct = Math.min(100, (view.scores[seat]! / target) * 100);
    return (
      <div key={seat} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="num" style={{ minWidth: 70, fontSize: 'var(--fs-xs)', color: '#fff' }}>
          {seat === me ? t('cg.you') : name(seat)} {view.scores[seat]}
        </span>
        <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.15)', overflow: 'hidden' }} role="progressbar" aria-valuemin={0} aria-valuemax={target} aria-valuenow={view.scores[seat]}>
          <div style={{ width: `${pct}%`, height: '100%', background: seat === me ? 'var(--accent)' : '#f59e0b', transition: 'width .4s' }} />
        </div>
      </div>
    );
  };

  const showPanel = (s: CribbageShow) => {
    const summary = ITEM_ORDER.map((type) => {
      const items = s.items.filter((i) => i.type === type);
      return items.length === 0 ? null : { type, n: items.length, points: items.reduce((p, i) => p + i.points, 0) };
    }).filter(Boolean) as { type: (typeof ITEM_ORDER)[number]; n: number; points: number }[];
    return (
      <div key={s.who} style={{ marginTop: 6 }}>
        <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
          {s.who === 'crib' ? t('cg.crib', { name: name(s.seat) }) : name(s.seat)} <span className="pill accent">+{s.points}</span>
        </div>
        <div className="melds" style={{ justifyContent: 'center' }}>
          <div className="meld">
            {s.cards.map((c) => (
              <PlayingCard key={c} id={c} size="sm" />
            ))}
          </div>
        </div>
        <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
          {summary.length === 0 ? t('cg.nineteen') : summary.map((x) => `${t(`cg.i.${x.type}` as never)} ${x.n > 1 ? `×${x.n} ` : ''}= ${x.points}`).join(' · ')}
        </div>
      </div>
    );
  };

  const pegNote = view.lastPeg && view.phase !== 'DISCARD' ? `${name(view.lastPeg.seat)} +${view.lastPeg.points} ${view.lastPeg.reasons.map(reasonLabel).join(', ')}` : null;

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={2}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.scores[s] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">{t('cg.dealer')}</span>}
                {s !== mySeat && view.phase !== 'SHOW' && <span className="tag">{view.handCounts[s]}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div style={{ width: '88%', display: 'grid', gap: 4 }} aria-label={t('table.scores')}>
            {track(opp)}
            {track(me)}
          </div>
          <div className="center-note">
            {t('sp.round', { n: view.round })} · {t('ce.goal', { n: target })}
            {view.phase === 'DISCARD' && !myTurn && turn?.seats[0] !== undefined && ` · ${t('table.waitingFor', { name: name(turn.seats[0]) })}`}
          </div>
          {view.phase === 'SHOW' || view.phase === 'ROUND_END' ? (
            <div className="card glass" style={{ textAlign: 'center', maxHeight: '44vh', overflow: 'auto' }} role="status">
              {view.starter && (
                <div className="row" style={{ justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('cg.starter')}</span>
                  <PlayingCard id={view.starter} size="sm" />
                </div>
              )}
              {view.shown.map(showPanel)}
              {view.phase === 'SHOW' && !lastShow && <span className="dim">{t('cg.counting')}</span>}
            </div>
          ) : (
            <>
              <div className="row" style={{ gap: 14, alignItems: 'center', justifyContent: 'center' }}>
                <div className="col" style={{ alignItems: 'center', gap: 2 }}>
                  {view.starter ? <PlayingCard id={view.starter} size={narrow ? 'sm' : 'md'} /> : <PlayingCard faceDown size={narrow ? 'sm' : 'md'} />}
                  <span className="center-note">{t('cg.starter')}</span>
                </div>
                <div className="col" style={{ alignItems: 'center', gap: 2 }}>
                  <PlayingCard faceDown size={narrow ? 'sm' : 'md'} />
                  <span className="center-note">{t('cg.crib', { name: name(view.dealer) })} · {view.cribCount}</span>
                </div>
              </div>
              {view.phase === 'PEGGING' && (
                <div className="col" style={{ alignItems: 'center', gap: 4 }} aria-label={t('cg.count', { n: view.count })}>
                  <div className="melds" style={{ justifyContent: 'center' }}>
                    <div className="meld">
                      {view.pile.map((c) => (
                        <PlayingCard key={c} id={c} size="sm" />
                      ))}
                    </div>
                  </div>
                  <strong style={{ color: '#fff' }}>{t('cg.count', { n: view.count })}</strong>
                </div>
              )}
            </>
          )}
          {pegNote && <div className="pill accent" role="status">{pegNote}</div>}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.scores[mySeat] ?? 0} pts</> : null} />
      </div>
      <div className="table-bottom">
        <div className="action-bar">
          {discarding && (
            <button className="btn primary" disabled={picked.length !== 2 || acting} onClick={() => void act({ type: 'discard', cards: [picked[0]!, picked[1]!] })}>
              {t('cg.toCrib', { n: picked.length })}
              {picked.length === 2 ? ` ${shortCard(picked[0]!)} ${shortCard(picked[1]!)}` : ''}
            </button>
          )}
          {view.phase === 'PEGGING' && myTurn && <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)' }}>{t('cg.playHint')}</span>}
        </div>
        <Hand size={narrow ? 'md' : 'lg'} label="Your hand" maxStep={0.6}>
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size={narrow ? 'md' : 'lg'}
              dealIndex={view.phase === 'DISCARD' ? i : undefined}
              selected={picked.includes(c)}
              playable={view.phase === 'PEGGING' && myTurn && playable.has(c)}
              unplayable={view.phase === 'PEGGING' && myTurn && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1].map((s) => (
                <th key={s} className="ellipsis" style={{ maxWidth: 110 }}>{name(s)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.roundResults.map((r) => (
              <tr key={r.round}>
                <td>D{r.round}</td>
                {r.gains.map((g, s) => (
                  <td key={s} className="num">
                    +{g}
                    {r.dealer === s && <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('cg.dealer')}</div>}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.totals.map((v, s) => (
                <td key={s} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('cg.scoreHint', { target })}</p>
      </Modal>
    </>
  );
}
